/**
 * Bobo's cameo: now and then he pops into a bottom corner for a few seconds, waves and says something short — a
 * tip about the current screen, a witty line or a greeting. Mounted once in the navigator; headless until it is
 * time (rules in `cameoLogic`), the wrapper is `box-none` so it never blocks the screen beneath.
 *
 * Touching him pauses the timer, tapping Bobo gives one more line and +4 s, X closes him for this session and
 * "Don't show again" turns him off for good (AsyncStorage, `bobo.cameo.*`).
 *
 * @module components/bobo/cameo/BoboCameo
 */

import React, {useCallback, useEffect, useRef, useState} from 'react';
import {AccessibilityInfo, Animated, AppState, Keyboard, Pressable, StyleSheet, Text, View, useWindowDimensions} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import {useTranslation} from 'react-i18next';
import {navigationRef} from '../../../helpers/navigationRef';
import BoboMascot from '../BoboMascot';
import BoboBubble from '../BoboBubble';
import {EXTEND_MS, VISIBLE_MS, dayKey, pickCameoMessage, resolveCameoTopic, shouldShowCameo} from './cameoLogic';

const STORAGE_KEY = 'bobo.cameo.state';
const TICK_MS = 5000;
const BOBO_SIZE = 72;
const MAX_SEEN = 40;

const loadState = async () => {
    try {
        const raw = await AsyncStorage.getItem(STORAGE_KEY);
        const parsed = raw ? JSON.parse(raw) : null;

        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (_e) {
        return {};
    }
};

/**
 * @param {Object} props Props.
 * @param {boolean} [props.enabled] Only for signed-in sessions.
 * @returns {(JSX.Element|null)} The overlay.
 */
const BoboCameo = ({enabled = true}) => {
    const {t} = useTranslation();
    const insets = useSafeAreaInsets();
    const {width: windowWidth} = useWindowDimensions();
    const [message, setMessage] = useState(null);
    const [calm, setCalm] = useState(false);
    const progress = useRef(new Animated.Value(0)).current;
    const stored = useRef({disabled: false, lastShownAt: null, shownDay: null, shownToday: 0, seen: []});
    const loaded = useRef(false);
    const session = useRef({snoozed: false, foregroundMs: 0, keyboard: false, reduced: false, route: null});
    const hideTimer = useRef(null);
    const deadline = useRef(0);
    const visible = useRef(false);

    const persist = useCallback(() => {
        AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(stored.current)).catch(() => {});
    }, []);

    const clearHide = () => {
        globalThis.clearTimeout(hideTimer.current);
        hideTimer.current = null;
    };

    const hide = useCallback(() => {
        clearHide();
        if (!visible.current) {
            return;
        }
        visible.current = false;
        Animated.timing(progress, {toValue: 0, duration: 260, useNativeDriver: true}).start(({finished}) => {
            if (finished && !visible.current) {
                setMessage(null);
            }
        });
    }, [progress]);

    const armHide = useCallback((ms) => {
        clearHide();
        deadline.current = Date.now() + ms;
        hideTimer.current = globalThis.setTimeout(hide, ms);
    }, [hide]);

    const pick = useCallback(() => {
        const now = new Date();
        const result = pickCameoMessage({
            topic: resolveCameoTopic(session.current.route),
            hour: now.getHours(),
            seenIds: stored.current.seen,
        });

        stored.current.seen = result.reset ? [result.id] : [...stored.current.seen, result.id].slice(-MAX_SEEN);

        return result;
    }, []);

    const show = useCallback(() => {
        const now = Date.now();
        const day = dayKey(now);

        stored.current.shownToday = stored.current.shownDay === day ? (stored.current.shownToday || 0) + 1 : 1;
        stored.current.shownDay = day;
        stored.current.lastShownAt = now;
        const next = pick();

        persist();
        visible.current = true;
        setCalm(session.current.reduced);
        setMessage(next);
        progress.setValue(0);
        Animated.timing(progress, {toValue: 1, duration: session.current.reduced ? 160 : 420, useNativeDriver: true}).start();
        armHide(VISIBLE_MS);
    }, [armHide, persist, pick, progress]);

    // ---- Load persisted state, listen to reduced motion + keyboard + foreground ------------------
    useEffect(() => {
        let cancelled = false;

        loadState().then((s) => {
            if (!cancelled) {
                stored.current = {...stored.current, ...s, seen: Array.isArray(s.seen) ? s.seen : []};
                loaded.current = true;
            }
        });
        AccessibilityInfo.isReduceMotionEnabled?.().then((v) => { session.current.reduced = !!v; }).catch(() => {});
        const motionSub = AccessibilityInfo.addEventListener?.('reduceMotionChanged', (v) => { session.current.reduced = !!v; });
        const showSub = Keyboard.addListener('keyboardDidShow', () => { session.current.keyboard = true; });
        const hideSub = Keyboard.addListener('keyboardDidHide', () => { session.current.keyboard = false; });

        return () => {
            cancelled = true;
            motionSub?.remove?.();
            showSub.remove();
            hideSub.remove();
        };
    }, []);

    // ---- Foreground clock + scheduler ------------------------------------------------------------
    useEffect(() => {
        if (!enabled) {
            return undefined;
        }
        const syncRoute = () => {
            try {
                session.current.route = navigationRef.isReady() ? navigationRef.getCurrentRoute()?.name || null : null;
            } catch (_e) {
                session.current.route = null;
            }
            // A task flow started while he is on screen: leave at once.
            if (visible.current && shouldShowCameo({}, Date.now(), {appActive: true, routeName: session.current.route}).reason === 'route') {
                hide();
            }
        };
        const unsubscribe = navigationRef.addListener?.('state', syncRoute);
        const interval = globalThis.setInterval(() => {
            if (AppState.currentState !== 'active') {
                return;
            }
            session.current.foregroundMs += TICK_MS;
            syncRoute();
            if (!loaded.current || visible.current) {
                return;
            }
            const decision = shouldShowCameo({
                ...stored.current,
                snoozed: session.current.snoozed,
                foregroundMs: session.current.foregroundMs,
            }, Date.now(), {
                appActive: true,
                keyboardVisible: session.current.keyboard,
                routeName: session.current.route,
                reducedMotion: session.current.reduced,
            });

            if (decision.show) {
                show();
            }
        }, TICK_MS);
        const appSub = AppState.addEventListener('change', (state) => {
            if (state !== 'active') {
                hide();
            }
        });

        return () => {
            globalThis.clearInterval(interval);
            unsubscribe?.();
            appSub.remove();
            clearHide();
        };
    }, [enabled, hide, show]);

    const onTap = useCallback(() => {
        setMessage(pick());
        persist();
        armHide(Math.max(0, deadline.current - Date.now()) + EXTEND_MS);
    }, [armHide, persist, pick]);

    const onClose = useCallback(() => {
        session.current.snoozed = true;
        hide();
    }, [hide]);

    const onDisable = useCallback(() => {
        stored.current.disabled = true;
        persist();
        hide();
    }, [hide, persist]);

    const pause = useCallback(() => clearHide(), []);
    const resume = useCallback(() => {
        if (visible.current) {
            armHide(Math.max(2500, deadline.current - Date.now()));
        }
    }, [armHide]);

    if (!enabled || !message) {
        return null;
    }
    const slide = calm ? 0 : 24;
    const animated = {
        opacity: progress,
        transform: [{translateY: progress.interpolate({inputRange: [0, 1], outputRange: [slide, 0]})}],
    };
    const width = Math.min(340, windowWidth - 24);

    return (
        <View pointerEvents="box-none" style={[styles.wrap, {bottom: insets.bottom + 16}]}>
            <Animated.View
                style={[styles.card, {width}, animated]}
                onTouchStart={pause}
                onTouchEnd={resume}
                onTouchCancel={resume}
            >
                <Pressable onPress={onTap} accessibilityRole="button" accessibilityLabel={t('boboCameo_a11y_bobo')}>
                    <BoboMascot
                        size={BOBO_SIZE}
                        mood={calm ? null : 'greet'}
                        interactive={false}
                        idle={false}
                        label={t('boboCameo_a11y_bobo')}
                    />
                </Pressable>
                <View style={styles.bubbleCol}>
                    <BoboBubble title={t(message.titleKey)} text={t(message.textKey)} placement="side" style={styles.bubble} />
                    <View style={styles.actions}>
                        <Pressable onPress={onDisable} hitSlop={8} accessibilityRole="button">
                            <Text style={styles.link}>{t('boboCameo_dontShow')}</Text>
                        </Pressable>
                    </View>
                </View>
                <Pressable
                    onPress={onClose}
                    hitSlop={10}
                    style={styles.close}
                    accessibilityRole="button"
                    accessibilityLabel={t('boboCameo_close')}
                >
                    <Text style={styles.closeText}>×</Text>
                </Pressable>
            </Animated.View>
        </View>
    );
};

const styles = StyleSheet.create({
    wrap: {position: 'absolute', left: 12, right: 12, alignItems: 'flex-start', zIndex: 900},
    card: {flexDirection: 'row', alignItems: 'flex-end', gap: 10},
    bubbleCol: {flex: 1, minWidth: 0},
    bubble: {flex: 0},
    actions: {flexDirection: 'row', justifyContent: 'flex-end', marginTop: 4},
    link: {fontSize: 11.5, color: '#64748B', textDecorationLine: 'underline'},
    close: {
        position: 'absolute', top: -10, right: -4, width: 24, height: 24, borderRadius: 12,
        backgroundColor: '#FFFFFF', borderWidth: 1, borderColor: '#FED7AA', alignItems: 'center', justifyContent: 'center',
    },
    closeText: {fontSize: 16, lineHeight: 18, color: '#7C2D12', fontWeight: '700'},
});

export default BoboCameo;
